import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxPollAttempts = 5;

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      toast.error('Invalid payment session');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async (attempt = 0) => {
    if (attempt >= maxPollAttempts) {
      setStatus('error');
      toast.error('Payment verification timed out. Please check your subscriptions page.');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/api/subscriptions/checkout-status`,
        { session_id: sessionId },
        { withCredentials: true }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('🎉 Payment successful! Your subscription is now active.');
        
        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/subscriptions');
        }, 3000);
      } else if (response.data.status === 'expired') {
        setStatus('error');
        toast.error('Payment session expired. Please try again.');
      } else {
        // Continue polling
        setPollAttempts(attempt + 1);
        setTimeout(() => pollPaymentStatus(attempt + 1), 2000);
      }
    } catch (error) {
      console.error('Payment status check error:', error);
      if (attempt < maxPollAttempts - 1) {
        // Retry
        setPollAttempts(attempt + 1);
        setTimeout(() => pollPaymentStatus(attempt + 1), 2000);
      } else {
        setStatus('error');
        toast.error('Failed to verify payment. Please contact support.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">
            {status === 'checking' && 'Verifying Payment...'}
            {status === 'success' && 'Payment Successful!'}
            {status === 'error' && 'Payment Verification Failed'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-indigo-600 animate-spin" />
              <p className="text-gray-600">
                Please wait while we verify your payment...
              </p>
              <p className="text-sm text-gray-500">
                Attempt {pollAttempts + 1} of {maxPollAttempts}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle2 className="w-16 h-16 mx-auto text-green-600" />
              <div className="space-y-2">
                <p className="text-lg font-semibold text-gray-900">
                  Your subscription is now active!
                </p>
                <p className="text-gray-600">
                  Welcome to HealthLoop! You'll be redirected to your subscriptions page.
                </p>
              </div>
              <Button
                onClick={() => navigate('/subscriptions')}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
              >
                View My Subscriptions
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <AlertCircle className="w-16 h-16 mx-auto text-red-600" />
              <div className="space-y-2">
                <p className="text-lg font-semibold text-gray-900">
                  Unable to verify payment
                </p>
                <p className="text-gray-600">
                  Please check your subscriptions page or contact support if you were charged.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => navigate('/subscriptions')}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                >
                  View Subscriptions
                </Button>
                <Button
                  onClick={() => navigate('/plans')}
                  variant="outline"
                  className="flex-1"
                >
                  Try Again
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
